import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import RouteSkeleton from "@/components/RouteSkeleton";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import { SectionCard, FormRow, PrimaryButton, Divider } from "@/components/ds";

// ─── Types ─────────────────────────────────────────────────────────────────

interface GA4Config {
  id: number;
  siteId: number;
  teamId: number;
  measurementId: string;
  apiSecretMasked: string | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string | null;
}

interface TestResult {
  valid: boolean;
  messages?: string[];
  error?: string;
}

// ─── Main Component ─────────────────────────────────────────────────────────
// Site-wide default config only (TeamId=0) — per-team overrides are stored on
// the same table server-side but this first-cut UI manages the one default row.

export default function GA4Analytics() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<GA4Config | null>(null);
  const [measurementId, setMeasurementId] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tab, setTab] = useState<"config" | "log">("config");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.admin.get("GA4/Configs");
      const data = await resp.json();
      const configs: GA4Config[] = Array.isArray(data) ? data : [];
      // TeamId=0 is the site-wide default row.
      const siteDefault = configs.find((c) => c.teamId === 0) ?? null;
      setConfig(siteDefault);
      setMeasurementId(siteDefault?.measurementId ?? "");
    } catch (err) {
      setSaveError(`Failed to load GA4 configuration: ${err instanceof Response ? (err as Response).status : String(err)}`);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setTestResult(null);
    try {
      const body: Record<string, unknown> = { measurementId: measurementId.trim() };
      if (apiSecret.trim()) body.apiSecret = apiSecret.trim();

      const resp = config
        ? await api.admin.put(`GA4/Configs/${config.id}`, body)
        : await api.admin.post("GA4/Configs", body);

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setSaveError(err?.error ?? `Save failed (${resp.status}).`);
        return;
      }

      setApiSecret(""); // clear — server never returns it, masked value shows from status
      await load();
    } catch (err) {
      setSaveError(`Failed to save: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleEnabled() {
    if (!config) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await api.admin.put(`GA4/Configs/${config.id}`, { isEnabled: !config.isEnabled });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setSaveError(err?.error ?? `Update failed (${resp.status}).`);
        return;
      }
      await load();
    } catch (err) {
      setSaveError(`Failed to update: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const body = config
        ? { configId: config.id }
        : { measurementId: measurementId.trim(), apiSecret: apiSecret.trim() };
      const resp = await api.admin.post("GA4/Configs/Test", body);
      const data: TestResult = await resp.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ valid: false, error: `Test failed: ${err instanceof Response ? (err as Response).status : String(err)}` });
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return <RouteSkeleton />;
  }

  const canSave = measurementId.trim().length > 0 && (apiSecret.trim().length > 0 || !!config);
  const canTest = (measurementId.trim().length > 0 && apiSecret.trim().length > 0) || !!config;

  const tabsJSX = (
    <>
      <IntegrationTab active={tab === "config"} onClick={() => setTab("config")}>Configuration</IntegrationTab>
      <IntegrationTab active={tab === "log"} onClick={() => setTab("log")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Google Analytics 4" tabs={tabsJSX}>
      {tab === "config" && (
        <div className="max-w-2xl">
          <SectionCard
            title="Measurement Protocol"
            description="Server-side event tracking — sends chat lifecycle events (started, queued, agent joined, resolved, missed) directly to GA4, no client-side script required."
            action={
              config ? (
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${config.isEnabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {config.isEnabled ? "Enabled" : "Disabled"}
                </span>
              ) : (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-500">
                  Not connected
                </span>
              )
            }
          >
            <FormRow label="Measurement ID" hint="From GA4 Admin → Data Streams → your web stream (e.g. G-XXXXXXXXXX)">
              <input
                id="ga4-measurement-id"
                type="text"
                value={measurementId}
                onChange={(e) => { setMeasurementId(e.target.value); setTestResult(null); setSaveError(null); }}
                placeholder="G-XXXXXXXXXX"
                aria-label="GA4 Measurement ID"
                className="w-64 text-sm px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono"
              />
            </FormRow>

            <FormRow
              label="API Secret"
              hint={config ? "Leave blank to keep the current secret" : "From GA4 Admin → Data Streams → your stream → Measurement Protocol API secrets"}
            >
              <input
                id="ga4-api-secret"
                type="password"
                value={apiSecret}
                onChange={(e) => { setApiSecret(e.target.value); setTestResult(null); setSaveError(null); }}
                placeholder={config?.apiSecretMasked ?? "Paste API secret"}
                autoComplete="new-password"
                aria-label="GA4 Measurement Protocol API Secret"
                className="w-64 text-sm px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono"
              />
            </FormRow>

            {saveError && (
              <div className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800 mt-3" role="alert">
                {saveError}
              </div>
            )}
            {testResult && (
              <div
                className={`rounded-lg p-3 text-sm mt-3 ${testResult.valid ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}
                role="status"
              >
                {testResult.valid
                  ? "Valid — GA4 accepted the Measurement ID and API secret."
                  : (testResult.error ?? testResult.messages?.join(" ") ?? "GA4 rejected these values.")}
              </div>
            )}

            <Divider className="my-4" />

            <div className="flex items-center gap-3 flex-wrap">
              <PrimaryButton onClick={handleSave} disabled={saving || !canSave}>
                {saving ? "Saving…" : config ? "Save Changes" : "Connect GA4"}
              </PrimaryButton>
              <button
                type="button"
                onClick={handleTest}
                disabled={testing || !canTest}
                className="px-4 py-2 rounded-xl text-sm font-medium border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? "Testing…" : "Test Connection"}
              </button>
              {config && (
                <button
                  type="button"
                  onClick={handleToggleEnabled}
                  disabled={saving}
                  className="text-sm text-zinc-500 hover:text-zinc-700 ml-auto disabled:opacity-50"
                >
                  {config.isEnabled ? "Disable tracking" : "Re-enable tracking"}
                </button>
              )}
            </div>
          </SectionCard>

          <SectionCard className="mt-4">
            <p className="text-xs text-zinc-600">
              <strong className="text-zinc-700">Events sent:</strong> PreChat, Queue, Chat, PostChat, MissedChat,
              PrechatAbandon — mirroring GA4 dashboards used on v10. Already using GTM for GA4? You don't need this —
              see the <strong>Google Tag Manager</strong> integration instead. This is for server-side tracking
              that fires even when the visitor blocks client-side analytics scripts.
            </p>
          </SectionCard>
        </div>
      )}

      {tab === "log" && (
        <IntegrationActivityLog integrationSlug="GA4" />
      )}
    </StickyRouteHeader>
  );
}
